import { roleHierarchy, type UserProfile } from "@/dtos/user";
import { axios } from "@/libs/axios";
import { keycloak } from "@/libs/keycloak";
import type { AxiosInstance } from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { toast } from "sonner";

interface AuthContextType {
  user: UserProfile | null;
  logout: () => void;
  isLoading: boolean;
  axios: AxiosInstance;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (keycloak.didInitialize) {
      return;
    }

    (async () => {
      try {
        const authenticated = await keycloak.init({ onLoad: "login-required" });
        if (authenticated) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${
            keycloak.token ?? ""
          }`;
          const user = await keycloak.loadUserProfile();
          const createdAt = user.createdTimestamp
            ? new Date(user.createdTimestamp).toISOString()
            : new Date().toISOString();

          let role: UserProfile["role"] = "member";
          const tokenParsed = keycloak.tokenParsed;

          if (
            tokenParsed &&
            tokenParsed.resource_access &&
            Object.hasOwn(tokenParsed.resource_access, "ethnos")
          ) {
            const ethnosRoles = tokenParsed.resource_access.ethnos
              .roles as Array<UserProfile["role"]>;

            role = ethnosRoles.reduce((highest, current) =>
              roleHierarchy[current] > roleHierarchy[highest]
                ? current
                : highest
            );

            setUser({
              role,
              id: user.id ?? "",
              email: user.email ?? "",
              name: user.username ?? "",
              created_at: createdAt,
            });
          }
        }
      } catch (error) {
        toast.error("Something went wrong, please try again", {
          richColors: true,
        });
        console.error(
          new Error("failed to initialize keycloak adapter", { cause: error })
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await keycloak.logout();
      toast.success("Logout successful", { richColors: true });
    } catch (error) {
      toast.error("Logout failed, please try again", { richColors: true });
      console.error(new Error("failed to logout", { cause: error }));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = useMemo((): AuthContextType => {
    return {
      isLoading,
      user,
      logout,
      axios,
    };
  }, [isLoading, logout, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }
  return context;
}

export { AuthProvider, useAuthContext };
